import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, Download, Loader2, MapPin, Shield, Heart, 
  Briefcase, Home, Plane, DollarSign, GraduationCap, Users,
  Wifi, Calculator, Video, Phone, Building, Clock, Globe,
  FileText, ExternalLink, FileDown, Share2
} from "lucide-react";
import RegionalVibeCheck from "../components/report/RegionalVibeCheck";
import EnergyGridCard from "../components/report/EnergyGridCard";

const SECTION_ICONS = {
  "Executive Summary": MapPin,
  "Relocation Timeline with Benchmarks": Clock,
  "Relocation Timeline": Clock,
  "A Day in the Life": Video,
  "Hobbies & Interests Support": Heart,
  "Hobbies & Interests": Heart,
  "Step-by-Step Relocation Process": FileText,
  "Visa & Immigration Requirements": Plane,
  "Visa & Immigration": Plane,
  "Cost of Living": DollarSign,
  "Healthcare System": Heart,
  "Healthcare Comparison": Heart,
  "Healthcare": Heart,
  "Real Estate Laws for Foreigners": Home,
  "Real Estate": Home,
  "Banking & Finance": Briefcase,
  "Banking": Briefcase,
  "Tax Information for Expats": Calculator,
  "Taxes for Expats": Calculator,
  "Taxes": Calculator,
  "Internet Speed & Access": Wifi,
  "Internet & Remote Work": Wifi,
  "Daily Life": Users,
  "Work & Business": Briefcase,
  "Work Opportunities": Briefcase,
  "Education": GraduationCap,
  "Education & Schools": GraduationCap,
  "Practical Resources": Globe
};

const ReportPage = () => {
  const navigate = useNavigate();
  const { countryName } = useParams();
  const { token } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sharing, setSharing] = useState(false);

  const generateReport = useCallback(async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/reports/generate/${encodeURIComponent(countryName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReport(response.data);
      toast.success("Report generated successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to generate report";
      toast.error(message);
      if (error.response?.status === 403) {
        navigate("/results");
      }
    } finally {
      setGenerating(false);
    }
  }, [countryName, token, navigate]);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await axios.get(
          `${API}/reports/country/${encodeURIComponent(countryName)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.content) {
          setReport(response.data);
        } else {
          generateReport();
        }
      } catch (error) {
        if (error.response?.status === 404) {
          generateReport();
        } else {
          toast.error("Failed to load report");
          navigate("/dashboard");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [countryName, token, navigate, generateReport]);

  const handleShare = async () => {
    if (!report?.id) {
      toast.error("Unable to share — report not loaded yet");
      return;
    }
    setSharing(true);
    try {
      const response = await axios.post(
        `${API}/reports/${report.id}/share`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const shareUrl = response.data.share_url;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Share link copied to clipboard!");
      } catch (clipErr) {
        toast.success(`Share link ready: ${shareUrl}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create share link");
    } finally {
      setSharing(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API}/reports/country/${encodeURIComponent(countryName)}/pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ExpatMatch_Guide_${countryName.replace(/ /g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownload = () => {
    if (!report?.content) return;
    
    let textContent = `# ExpatMatch Comprehensive Relocation Report: ${countryName}\n`;
    textContent += `${"=".repeat(60)}\n`;
    textContent += `Generated: ${new Date(report.generated_at).toLocaleDateString()}\n\n`;
    
    if (report.content.quick_facts) {
      textContent += "## QUICK FACTS\n";
      textContent += "-".repeat(40) + "\n";
      report.content.quick_facts.forEach(fact => {
        textContent += `• ${fact}\n`;
      });
      textContent += "\n";
    }

    if (report.content.key_stats) {
      textContent += "## KEY STATISTICS\n";
      textContent += "-".repeat(40) + "\n";
      Object.entries(report.content.key_stats).forEach(([key, value]) => {
        textContent += `• ${key.replace(/_/g, " ").toUpperCase()}: ${value}\n`;
      });
      textContent += "\n";
    }
    
    if (report.content.sections) {
      report.content.sections.forEach(section => {
        textContent += `\n## ${section.title.toUpperCase()}\n`;
        textContent += "=".repeat(50) + "\n\n";
        textContent += `${section.content}\n\n`;
        
        if (section.subsections) {
          section.subsections.forEach(sub => {
            textContent += `### ${sub.title}\n`;
            textContent += "-".repeat(30) + "\n";
            textContent += `${sub.content}\n\n`;
          });
        }
      });
    }

    if (report.content.video_links) {
      textContent += "\n## VIDEO RESOURCES\n";
      textContent += "=".repeat(50) + "\n";
      report.content.video_links.forEach(video => {
        textContent += `• ${video.title}: ${video.url}\n`;
        textContent += `  ${video.description}\n\n`;
      });
    }

    if (report.content.important_contacts) {
      textContent += "\n## IMPORTANT CONTACTS\n";
      textContent += "=".repeat(50) + "\n";
      report.content.important_contacts.forEach(contact => {
        textContent += `\n${contact.name}\n`;
        textContent += `Address: ${contact.address}\n`;
        textContent += `Phone: ${contact.phone}\n`;
        if (contact.website) textContent += `Website: ${contact.website}\n`;
      });
    }
    
    textContent += `\n\n${"=".repeat(60)}\n`;
    textContent += "This report is for informational purposes only.\n";
    textContent += "Always verify information with official sources.\n";
    textContent += "© ExpatMatch - Your Relocation Partner\n";
    
    const blob = new Blob([textContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ExpatMatch_Report_${countryName.replace(/\s+/g, "_")}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Report downloaded!");
  };

  const keyStats = useMemo(() => {
    if (!report?.content?.key_stats) return [];
    return Object.entries(report.content.key_stats).filter(([key]) => !key.includes('_usa'));
  }, [report]);

  if (loading || generating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">
            {generating ? "Generating your comprehensive report..." : "Loading report..."}
          </p>
          {generating && (
            <p className="text-sm text-muted-foreground mt-2">
              This includes detailed information on visas, real estate laws, taxes, and more...
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!report?.content) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-primary mb-4">Report Not Available</h2>
          <p className="text-muted-foreground mb-6">
            Unable to load the report for {countryName}.
          </p>
          <Button onClick={() => navigate("/dashboard")} data-testid="back-dashboard-btn">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const content = report.content;

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-primary mb-2" data-testid="report-title">
                {countryName}
              </h1>
              <p className="text-muted-foreground">
                Personalized Relocation Decision Guide
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={downloadPdf}
                disabled={downloadingPdf}
                className="btn-animated bg-primary hover:bg-primary/90 gap-2"
                data-testid="download-pdf-btn"
              >
                {downloadingPdf ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4" />
                )}
                Download PDF
              </Button>
              <Button
                onClick={handleShare}
                variant="outline"
                disabled={sharing}
                className="gap-2"
                data-testid="share-report-btn"
              >
                {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                Share
              </Button>
              <Button
                onClick={handleDownload}
                variant="outline"
                className="gap-2"
                data-testid="download-txt-btn"
              >
                <Download className="h-4 w-4" />
                Download Text
              </Button>
            </div>
          </div>
        </div>

        {/* Key Stats */}
        {content.key_stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {keyStats.map(([key, value]) => {
              const usaKey = `${key}_usa`;
              const usaValue = content.key_stats[usaKey];

              return (
                <div
                  key={key}
                  className="bg-card rounded-xl border border-primary/10 p-4 text-center"
                >
                  <p className="font-mono text-2xl font-bold text-primary">{value}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {key.replace(/_/g, " ")}
                  </p>
                  {usaValue !== undefined && (
                    <p className="text-xs text-accent mt-1">vs USA: {usaValue}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Residency Requirements */}
        {content.residency_requirements && (
          <div className="bg-gradient-to-r from-secondary/10 to-primary/10 rounded-xl p-6 mb-8 border border-secondary/20">
            <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Residency Financial Requirements
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-white/50 rounded-lg">
                <p className="font-mono text-2xl font-bold text-primary">
                  ${content.residency_requirements.minimum_savings_usd?.toLocaleString() || "Varies"}
                </p>
                <p className="text-sm text-muted-foreground">Minimum Savings Required</p>
              </div>
              <div className="text-center p-4 bg-white/50 rounded-lg">
                <p className="font-mono text-2xl font-bold text-secondary">
                  ${content.residency_requirements.monthly_income_required_usd?.toLocaleString() || "Varies"}/mo
                </p>
                <p className="text-sm text-muted-foreground">Monthly Income Required</p>
              </div>
              <div className="p-4 bg-white/50 rounded-lg">
                <p className="text-sm text-foreground/80">
                  {content.residency_requirements.notes || "Requirements vary by visa type"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Quick Facts */}
        {content.quick_facts && content.quick_facts.length > 0 && (
          <div className="bg-accent/10 rounded-xl p-6 mb-8 border border-accent/20">
            <h2 className="text-xl font-bold text-primary mb-4">Quick Facts</h2>
            <ul className="grid md:grid-cols-2 gap-3">
              {content.quick_facts.map((fact, i) => (
                <li key={`fact-${i}-${fact.slice(0, 20)}`} className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                  <span className="text-foreground/80">{fact}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Comparative Regional Analysis */}
        <RegionalVibeCheck regional={content.regional_analysis} />

        {/* Energy Grid Dependability */}
        <EnergyGridCard energy={content.energy_grid} />

        {/* Video Links */}
        {content.video_links && content.video_links.length > 0 && (
          <div className="bg-secondary/10 rounded-xl p-6 mb-8 border border-secondary/20">
            <div className="flex items-center gap-2 mb-4">
              <Video className="h-5 w-5 text-secondary" />
              <h2 className="text-xl font-bold text-primary">Video Resources</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {content.video_links.map((video, i) => (
                <a
                  key={video.url || `video-${i}`}
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 p-4 bg-white/50 rounded-lg border border-secondary/10 hover:border-secondary/30 transition-colors"
                  data-testid={`video-link-${i}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0">
                    <Video className="h-5 w-5 text-secondary" />
                  </div>
                  <div>
                    <p className="font-semibold text-primary flex items-center gap-1">
                      {video.title}
                      <ExternalLink className="h-3 w-3" />
                    </p>
                    <p className="text-sm text-muted-foreground">{video.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Table of Contents */}
        {content.sections && (
          <div className="bg-card rounded-xl border border-primary/10 p-6 mb-8">
            <h2 className="text-lg font-bold text-primary mb-4">Table of Contents</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
              {content.sections.map((section, index) => (
                <button
                  key={section.title || `toc-${index}`}
                  onClick={() => setActiveSection(index)}
                  className={`text-left p-2 rounded-lg text-sm transition-colors ${
                    activeSection === index 
                      ? "bg-primary/10 text-primary" 
                      : "hover:bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {index + 1}. {section.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Report Sections */}
        <div className="space-y-8">
          {content.sections?.map((section, index) => {
            const IconComponent = SECTION_ICONS[section.title] || MapPin;
            
            return (
              <div 
                key={section.title || `section-${index}`}
                id={`section-${index}`}
                className="bg-card rounded-2xl border border-primary/10 p-8"
                data-testid={`section-${index}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="h-5 w-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-primary">{section.title}</h2>
                </div>
                
                <div className="prose prose-stone max-w-none">
                  <p className="text-foreground/80 whitespace-pre-line leading-relaxed">
                    {section.content}
                  </p>
                </div>
                
                {section.subsections && section.subsections.length > 0 && (
                  <div className="mt-6 space-y-4 border-t border-primary/10 pt-6">
                    {section.subsections.map((sub, subIndex) => (
                      <div key={sub.title || `sub-${subIndex}`} className="pl-4 border-l-2 border-secondary/30">
                        <h3 className="text-lg font-semibold text-primary mb-2">
                          {sub.title}
                        </h3>
                        <p className="text-foreground/70 whitespace-pre-line">
                          {sub.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Important Contacts */}
        {content.important_contacts && content.important_contacts.length > 0 && (
          <div className="mt-8 bg-primary/5 rounded-2xl border border-primary/10 p-8">
            <div className="flex items-center gap-2 mb-6">
              <Phone className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-bold text-primary">Important Contacts</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {content.important_contacts.map((contact, i) => (
                <div 
                  key={contact.name || `contact-${i}`}
                  className="bg-white/70 rounded-xl p-4 border border-primary/10"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Building className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary">{contact.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{contact.address}</p>
                      <p className="text-sm text-foreground mt-1">
                        <span className="font-medium">Phone:</span> {contact.phone}
                      </p>
                      {contact.website && (
                        <a 
                          href={contact.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-secondary hover:underline flex items-center gap-1 mt-1"
                        >
                          Visit Website <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center text-muted-foreground text-sm">
          <p>Report generated on {new Date(report.generated_at).toLocaleDateString()}</p>
          <p className="mt-1">
            This report is for informational purposes only. Always verify information 
            with official sources before making relocation decisions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReportPage;
